import { NOTATION_10E18 } from './constants';

/**
 * 소수점이 있는 숫자에 10에 18승을 곱하는 처리를 한다.
 * 수 연산이 있을 경우 쓰레기 값이 포함되므로,
 * 문자열로 처리하고, 마지막에 숫자로 반환한다.
 * @param {number | String} value
 * @return {number}
 */
export const convertDecimalToBigNum = (value) => {
  const valueStr = value.toString();
  // 소수점 이하 자리수는 18자리이다.
  const decimalLength = NOTATION_10E18.toString().length - 1;
  // 정수부분과 소수부분을 분리한다.
  const split = valueStr.split('.');
  // 정수 부분
  const intPart = split[0];
  // 소수부분, decimalLength 보다 긴 경우는 자른다.
  const decimalPart = (split[1] ?? '').slice(0, decimalLength);
  // 자리수에서 모자라는 만큼 0을 추가한다.
  const attachZero = '0'.repeat(decimalLength - decimalPart.length);
  // 정수부분, 소수부분, 0부분을 합친다.
  const resultStr = [intPart, decimalPart, attachZero].join('');
  // 숫자로 반환
  return Number(resultStr);
};

/**
 * 큰 수를 10 ** 18만큼 나누어진 숫자 문자열을 반환한다.
 * @param {number | String} value // 지수 형태의 문자열로 들어올 수도 있다.
 * @param {number} fixed // 표시할 소수점 이하 자리수
 * @return {number}
 */
export const convertBigNumToDecimalString = (value, fixed) => {
  if (!value) {
    return '';
  }

  // 지수 변환 처리
  const valueStr = convertNotationToNumberString(`${value}`);
  // 지수 변환 처리를 했는데 소수점이 있으면 잘못된 데이터이다.
  if (valueStr.includes('.')) {
    throw Error(
      `토큰 개수에 소수점이 포함되어 있습니다. DB 데이터를 확인해 주세요. value=${value}`,
    );
  }
  const length = NOTATION_10E18.toString().length; //19자리

  // 숫자가 19자리보다 작은 경우 앞에 0을 삽입한다.
  let str = `${valueStr}`;
  while (str.length < length) {
    str = `0${str}`;
  }
  // 정수부분과 소수부분으로 나눈다. 소수 부분은 자리수를 적용한다.
  const integerLength = str.length - length + 1;
  const intPart = str.slice(0, integerLength);
  const decimalPart = str.slice(integerLength).slice(0, fixed ?? 0);
  // 소수 부분이 없으면 정수부분만 리턴, 소수부분이 있으면 소수점을 추가해서 리턴
  if (decimalPart.length) {
    return [intPart, decimalPart].join('.');
  }
  return intPart;
};

/**
 * 지수 형태의 문자열을 숫자 형태의 문자열로 변환
 * 지수 형태란 1.234e+10과 같은 형태를 말함 -> 12340000000 형태로 변환
 * 지수 중에는 1e+21과 같이 소수점이 없을 수도 있다.
 * @param {String} value
 * @return {String}
 */
const convertNotationToNumberString = (value) => {
  const split = value.split('e+');
  const notation = split[1];
  if (!notation || notation.length === 0) {
    return value;
  }
  const numberParts = split[0].split('.');
  const zeroStr = '0'.repeat(Number(notation) - (numberParts[1]?.length ?? 0));
  return [...numberParts, zeroStr].join('');
};
